import { useState, useMemo, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import type { SessionStatus, SessionModel } from '../api/types.js';
import { useAuth } from '../auth/index.js';
import { useServer } from '../hooks/use-servers.js';
import { useSessions, useCreateSession } from '../hooks/use-sessions.js';
import { useStartServer } from '../hooks/use-server-actions.js';
import { StatusBadge } from '../components/status-badge.js';
import { SessionCard } from '../components/session-card.js';
import { useToast } from '../hooks/use-toast.js';
import { Button } from '../components/button.js';
import { Select } from '../components/select.js';
import { ServerMenu } from '../components/server-menu.js';
import { PrReviews } from '../components/pr-reviews.js';
import { People } from '../components/people.js';
import { FileExplorer } from './file-explorer.js';
import { TicketPlans } from '../components/ticket-plans.js';
import { TicketPage } from '../components/ticket-page.js';
import { SlackConnection } from '../components/slack-connection.js';
import { SlackThreadPage } from '../components/slack-thread.js';
import { BudgetHeld } from '../components/budget-held.js';
import { useUrlFlag, useUrlState } from '../hooks/use-url-state.js';
import { isUnread, useMarkRead, useReadState } from '../hooks/use-read-state.js';
import { DeploymentApprovals } from '../components/deployment-approvals.js';
import { NotificationDelivery } from '../components/notification-delivery.js';
import { MonthlyCost } from '../components/monthly-cost.js';
import { ServerStatusLine } from '../components/server-nav.js';
import { CalendarChanges } from '../components/calendar-changes.js';
import { DocumentChanges } from '../components/document-changes.js';
import { ImprovementProposals } from '../components/improvement-proposals.js';
import { ActionProposals } from '../components/action-proposals.js';
import { MessagesToSend, ServerMessaging } from '../components/outbox-messages.js';
import { Inbox } from '../components/inbox.js';
import { ServerLogins } from '../components/server-logins.js';
import { ServerWaits } from '../components/server-waits.js';
import { SprintTab } from '../components/sprint-tab.js';
import { MailTab } from '../components/mail-tab.js';
import { MeetingsTab } from '../components/meetings-tab.js';
import { useSessionsAwaitingDecision } from '../hooks/use-calendar-proposals.js';
import { bySessionOrder, lastActive } from '../utils/session-order.js';
import { IndexStatus } from '../components/index-status.js';
import { SecretsScrub } from '../components/secrets-scrub.js';
import { ReportButton } from '../components/report-button.js';
import { useFacetSessions } from '../hooks/use-session-actions.js';

const STATUS_FILTERS: { value: SessionStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'idle', label: 'Idle' },
  { value: 'processing', label: 'Processing' },
  { value: 'waiting_for_input', label: 'Waiting' },
  { value: 'terminated', label: 'Terminated' },
];

const MODEL_OPTIONS: { value: SessionModel; label: string }[] = [
  { value: 'sonnet', label: 'Sonnet' },
  { value: 'opus', label: 'Opus' },
  { value: 'haiku', label: 'Haiku' },
];

type DatePreset = 'all' | 'today' | '7d' | '30d';
const PAGE_SIZE = 20;
const DATE_FILTERS: { value: DatePreset; label: string }[] = [
  { value: 'all', label: 'All time' },
  { value: 'today', label: 'Today' },
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
];

function getDateThreshold(preset: DatePreset): Date | null {
  if (preset === 'all') return null;
  const now = new Date();
  switch (preset) {
    case 'today':
      return new Date(now.getFullYear(), now.getMonth(), now.getDate());
    case '7d':
      return new Date(now.getTime() - 7 * 86_400_000);
    case '30d':
      return new Date(now.getTime() - 30 * 86_400_000);
  }
}

/** A server's pages (tabs): `sessions` (decisions and sessions), `reviews` (PRs and deployments), `people`, `files` (the workspace), `settings` */
export function ServerDetail({ tab = 'sessions' }: { tab?: 'sessions' | 'reviews' | 'people' | 'sprint' | 'mail' | 'meetings' | 'files' | 'settings' | 'ticket' | 'slack' }) {
  const { serverId } = useParams<{ serverId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: server, isLoading: serverLoading } = useServer(serverId!);
  const serverRunning = server?.status === 'running';

  // Filters live in the URL: they survive a reload, the back button and a shared link
  const [statusFilter, setStatusFilter] = useUrlState<SessionStatus | 'all'>('status', 'all', { allowed: STATUS_FILTERS.map((f) => f.value) });
  const [searchQuery, setSearchQuery] = useUrlState<string>('q', '', { replace: true });
  const [modelFilter, setModelFilter] = useUrlState<SessionModel | 'all'>('model', 'all', { allowed: ['all', ...MODEL_OPTIONS.map((m) => m.value)] });
  const [dateFilter, setDateFilter] = useUrlState<DatePreset>('date', 'all', { allowed: DATE_FILTERS.map((f) => f.value) });
  // A facet of the index (`technology:vpn`), from a session's facets: its sessions only (operator, 30/10/2026)
  const [facetFilter, setFacetFilter] = useUrlState<string>('facet', '', { replace: true });
  // A facet is `dimension:value`, both there: anything else is no facet, as the hook reads it
  const onFacet = facetFilter.indexOf(':') > 0;
  const { data: facetSessions, isError: facetError } = useFacetSessions(serverId!, facetFilter);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newSessionModel, setNewSessionModel] = useState<SessionModel>('sonnet');
  const [newSessionName, setNewSessionName] = useState('');
  const [showTerminated, setShowTerminated] = useUrlFlag('terminated');
  const [shown, setShown] = useUrlState<string>('show', String(PAGE_SIZE), { replace: true });
  const visibleCount = Math.max(PAGE_SIZE, Number.parseInt(shown, 10) || PAGE_SIZE);

  const sessionStatus = showTerminated ? 'all' : undefined;
  const { data: sessions, isLoading, isError, error } = useSessions(serverId!, !!server, sessionStatus);
  // Opened on a facet: the list is what was asked, brought into view once, when it is read and what is above it too
  const scrolledFor = useRef('');
  const facetReady = onFacet && !!facetSessions && !!sessions;
  useEffect(() => {
    if (!facetReady || scrolledFor.current === facetFilter) return;
    // Marked once it happened: a refresh of the list meanwhile does not cancel it for good
    const t = window.setTimeout(() => {
      scrolledFor.current = facetFilter;
      document.getElementById('sessions-title')?.scrollIntoView({ block: 'start', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }, 400);
    return () => window.clearTimeout(t);
  }, [facetReady, facetFilter]);
  const createSession = useCreateSession(serverId!);
  const startServer = useStartServer();
  const { toast } = useToast();

  const awaiting = useSessionsAwaitingDecision(serverId!);
  const isAdmin = user?.role === 'admin';
  const { data: readState } = useReadState(serverId!);
  const { markRead } = useMarkRead(serverId!);

  const dateThreshold = useMemo(() => getDateThreshold(dateFilter), [dateFilter]);

  const sorted = useMemo(() => {
    const filtered = sessions?.filter((s) => {
      if (statusFilter !== 'all' && s.status !== statusFilter) return false;
      // On a facet, nothing but its sessions: none while they are read, rather than all of them
      if (onFacet && !facetSessions?.sessionIds.includes(s.id)) return false;
      if (modelFilter !== 'all' && s.model !== modelFilter) return false;
      if (dateThreshold && new Date(lastActive(s)) < dateThreshold) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const nameMatch = s.name?.toLowerCase().includes(q) ?? false;
        const idMatch = s.id.toLowerCase().includes(q);
        if (!nameMatch && !idMatch) return false;
      }
      return true;
    });

    return filtered?.slice().sort(bySessionOrder);
  }, [sessions, statusFilter, modelFilter, searchQuery, dateThreshold, onFacet, facetSessions]);

  const unreadIds = useMemo(() => (sessions ?? []).filter((s) => isUnread(s, readState)).map((s) => s.id), [sessions, readState]);

  function handleCreate() {
    createSession.mutate(
      { model: newSessionModel, name: newSessionName || undefined },
      {
        onSuccess: (session) => {
          toast('Session created', 'success');
          setShowCreateDialog(false);
          setNewSessionName('');
          setNewSessionModel('sonnet');
          navigate(`/servers/${serverId}/sessions/${session.id}`);
        },
      },
    );
  }

  return (
    <div>
      <div className="mb-6">
        <Link to="/servers" className="text-sm" style={{ color: 'var(--color-text-3)' }} onMouseOver={(e) => (e.currentTarget.style.color = 'var(--color-text-2)')} onMouseOut={(e) => (e.currentTarget.style.color = 'var(--color-text-3)')}>
          &larr; Back to servers
        </Link>
        <div className="mt-2 flex items-center gap-3">
          <h1 className="page-title text-2xl">
            {server?.organization ?? serverId}
          </h1>
          {server && <StatusBadge status={server.status} />}
          {server && isAdmin && <ServerMenu server={server} />}
          {/* What happened over a period, written by a session (operator, 30/10/2026) */}
          {server && isAdmin && <ReportButton serverId={server.id} running={server.status === 'running'} />}
        </div>
        {server && (
          <p className="mt-0.5 text-sm" style={{ color: 'var(--color-text-3)' }}>{server.id} · {server.instanceType}</p>
        )}
        {server && <ServerStatusLine serverId={server.id} />}
      </div>

      {/* Server stopped banner */}
      {server && !serverRunning && !serverLoading && (
        <div className="mb-6 rounded-lg border p-4" style={{ borderColor: 'var(--color-warn)', backgroundColor: 'var(--color-warn-dim)' }}>
          <div className="flex items-center justify-between gap-3">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <svg className="h-5 w-5 flex-shrink-0" style={{ color: 'var(--color-warn)' }} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
                </svg>
                <p className="text-sm font-medium" style={{ color: 'var(--color-warn)' }}>
                  {server.status === 'stopped' ? 'Server is stopped' : `Server is ${server.status}`}
                </p>
              </div>
              {server.status === 'stopped' && (
                <p className="mt-1 ml-7 text-xs" style={{ color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)' }}>
                  {server.stopReason ?? 'Unknown'}
                  {server.stoppedBy && <span style={{ opacity: 0.6 }}>{` · ${server.stoppedBy}`}</span>}
                  {server.stoppedAt && <span style={{ opacity: 0.6 }}>{` · ${new Date(server.stoppedAt).toLocaleString()}`}</span>}
                </p>
              )}
            </div>
            {isAdmin && server.status === 'stopped' && (
              <Button
                onClick={() => startServer.mutate(server.id)}
                disabled={startServer.isPending}
                size="sm"
                style={{ backgroundColor: 'var(--color-success)', flexShrink: 0 }}
              >
                {startServer.isPending ? 'Starting...' : 'Start Server'}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Reviews: deployment approvals (ADR-022), ticket plans (ADR-026), then held PRs and prepared reviews (ADR-021) */}
      {server && tab === 'reviews' && (
        <>
          <DeploymentApprovals serverId={server.id} isAdmin={isAdmin} />
          <BudgetHeld serverId={server.id} isAdmin={isAdmin} />
          <TicketPlans serverId={server.id} isAdmin={isAdmin} />
          <PrReviews serverId={server.id} isAdmin={isAdmin} serverRunning={serverRunning} />
        </>
      )}

      {/* Settings: how this server notifies, its monthly cost, the calendar changes decided */}
      {/* The people of the server (ADR-030), for the operator */}
      {server && tab === 'people' && (isAdmin
        ? <People serverId={server.id} />
        : <p className="text-sm" style={{ color: 'var(--color-text-2)' }}>The people of a server are visible to admins only.</p>)}
      {/* The team's Jira sprint (ADR-034), for every member of the organisation */}
      {server && tab === 'sprint' && <SprintTab serverId={server.id} />}
      {/* One ticket (ADR-039 section 7), opened from To do */}
      {server && tab === 'ticket' && <TicketPage serverId={server.id} isAdmin={isAdmin} />}
      {/* A Slack conversation to answer (ADR-039 step 8), opened from To do */}
      {server && tab === 'slack' && <SlackThreadPage serverId={server.id} isAdmin={isAdmin} />}
      {server && tab === 'mail' && <MailTab serverId={server.id} isAdmin={isAdmin} />}
      {server && tab === 'meetings' && <MeetingsTab serverId={server.id} isAdmin={isAdmin} />}
      {/* The workspace's files, read from the running server */}
      {server && tab === 'files' && (serverRunning
        ? <FileExplorer serverId={server.id} />
        : <p className="text-sm" style={{ color: 'var(--color-text-2)' }}>The files are read from the server while it runs. Start it to browse them.</p>)}
      {server && tab === 'settings' && <ServerSettings serverId={server.id} isAdmin={isAdmin} />}

      {tab === 'sessions' && (<>
      {/* What waits on the operator, ranked (#760); hidden when nothing does */}
      {server && <Inbox serverId={server.id} />}

      {/* Calendar changes the sessions asked for, to decide: nothing changes until approved here (ADR-024) */}
      {server && <CalendarChanges serverId={server.id} isAdmin={isAdmin} part="pending" />}

      {/* Changes to documents aidev did not create: nothing is written until applied here (ADR-038 section 2b) */}
      {server && <DocumentChanges serverId={server.id} isAdmin={isAdmin} />}

      {/* Actions the sessions propose: a review, an analysis, a decision on a deployment (ADR-039 step 2) */}
      {server && <ActionProposals serverId={server.id} isAdmin={isAdmin} />}

      {/* Messages the sessions want to send to another server: nothing leaves until sent (ADR-025 step 2) */}
      {server && <MessagesToSend serverId={server.id} isAdmin={isAdmin} />}

      {/* Improvements to aidev the sessions propose: nothing is filed until opened as an issue (ADR-025) */}
      {server && <ImprovementProposals serverId={server.id} isAdmin={isAdmin} />}

      {/* The logins the sessions depend on, and when each ends (ADR-033) */}
      {server && isAdmin && <ServerLogins serverId={server.id} enabled={serverRunning} />}

      {/* What the sessions wait for: a commit's checks, a command the agent runs (#965, ADR-032) */}
      {server && isAdmin && <ServerWaits serverId={server.id} />}

      {/* Sessions section */}
      {server && (
        <div>
          <div className="flex items-center justify-between">
            <div className="flex items-baseline gap-3">
              <h2 id="sessions-title" className="scroll-mt-4 text-lg font-semibold" style={{ color: 'var(--color-text)' }}>Sessions</h2>
              {unreadIds.length > 0 && (
                <button type="button" onClick={() => markRead(unreadIds)} className="text-xs hover:underline" style={{ color: 'var(--color-brand-light)' }}>
                  Mark {unreadIds.length === 1 ? '1 unread session' : `${unreadIds.length} unread sessions`} as read
                </button>
              )}
            </div>
            {isAdmin && serverRunning && (
              <Button
                onClick={() => setShowCreateDialog(true)}
                size="sm"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                </svg>
                Create Session
              </Button>
            )}
          </div>

          {/* The facet the list is on, removable */}
          {onFacet && (
            <p className="mt-3 flex flex-wrap items-center gap-2 text-sm" style={{ color: 'var(--color-text-2)' }}>
              <span>
                {facetError ? 'The sessions of this facet were not read, about' : !facetSessions ? 'Reading the sessions about'
                  : `${sorted?.length === 1 ? 'One session' : `${sorted?.length ?? 0} sessions`} about`}
                {' '}<span style={{ color: 'var(--color-text-3)' }}>{facetFilter.slice(0, facetFilter.indexOf(':'))}</span> {facetFilter.slice(facetFilter.indexOf(':') + 1)}
              </span>
              <button type="button" onClick={() => setFacetFilter('')} className="text-xs hover:underline focus-visible:outline focus-visible:outline-2" style={{ color: 'var(--color-brand-light)' }}>
                All sessions
              </button>
            </p>
          )}

          {/* Search */}
          <div className="mt-4 relative">
            <svg
              className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2"
              style={{ color: 'var(--color-text-3)' }}
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
            </svg>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search sessions..."
              className="w-full rounded-lg border pl-9 pr-3 py-1.5 text-sm focus:outline-none focus:ring-1"
              style={{ borderColor: 'var(--color-border-2)', backgroundColor: 'var(--color-surface)', color: 'var(--color-text)', '--tw-ring-color': 'var(--color-brand)' } as React.CSSProperties}
            />
          </div>

          {/* Filters */}
          <div className="mt-3 flex flex-wrap gap-2">
            <Select
              size="sm"
              active={statusFilter !== 'all'}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as SessionStatus | 'all')}
              aria-label="Filter by status"
            >
              {STATUS_FILTERS.map(({ value, label }) => (
                <option key={value} value={value}>{value === 'all' ? 'All statuses' : label}</option>
              ))}
            </Select>
            <Select
              size="sm"
              active={modelFilter !== 'all'}
              value={modelFilter}
              onChange={(e) => setModelFilter(e.target.value as SessionModel | 'all')}
              aria-label="Filter by model"
            >
              <option value="all">All models</option>
              {MODEL_OPTIONS.map(({ value, label }) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </Select>
            <Select
              size="sm"
              active={dateFilter !== 'all'}
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value as DatePreset)}
              aria-label="Filter by date"
            >
              {DATE_FILTERS.map(({ value, label }) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </Select>
            {serverRunning && (
              <button
                type="button"
                onClick={() => setShowTerminated(!showTerminated)}
                className="rounded-md border px-2.5 py-1.5 text-[11px] font-medium tracking-wide transition-colors"
                style={{
                  fontFamily: 'var(--font-mono)',
                  borderColor: showTerminated ? 'var(--color-brand)' : 'var(--color-border-2)',
                  backgroundColor: showTerminated ? 'var(--color-brand-dim)' : 'var(--color-surface)',
                  color: showTerminated ? 'var(--color-brand)' : 'var(--color-text-2)',
                }}
              >
                Show terminated
              </button>
            )}
          </div>

          {/* Session list */}
          <div className="mt-4">
            {isLoading && (
              <div className="py-8 text-center text-sm" style={{ color: 'var(--color-text-3)' }}>Loading sessions...</div>
            )}
            {isError && (
              <div className="py-8 text-center text-sm" style={{ color: 'var(--color-error)' }}>
                Failed to load sessions: {error.message}
              </div>
            )}
            {sorted && sorted.length === 0 && (
              <div className="flex flex-col items-center gap-3 py-12">
                <svg className="h-10 w-10" style={{ color: 'var(--color-text-3)', opacity: 0.5 }} fill="none" viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 0 1-.825-.242m9.345-8.334a2.126 2.126 0 0 0-.476-.095 48.64 48.64 0 0 0-8.048 0c-1.131.094-1.976 1.057-1.976 2.192v4.286c0 .837.46 1.58 1.155 1.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455 48.455 0 0 0 11.25 3c-2.115 0-4.198.137-6.24.402-1.608.209-2.76 1.614-2.76 3.235v6.226c0 1.621 1.152 3.026 2.76 3.235.577.075 1.157.14 1.74.194V21l4.155-4.155" />
                </svg>
                <p className="text-sm" style={{ color: 'var(--color-text-3)' }}>
                  {statusFilter === 'all' ? 'No sessions yet' : `No ${statusFilter} sessions`}
                </p>
                {isAdmin && serverRunning && statusFilter === 'all' && (
                  <Button size="sm" onClick={() => setShowCreateDialog(true)}>
                    Create your first session
                  </Button>
                )}
              </div>
            )}
            {sorted && sorted.length > 0 && (
              <div className="space-y-2">
                {sorted.slice(0, visibleCount).map((session) => (
                  <SessionCard key={session.id} session={session} unread={isUnread(session, readState)} awaitingDecision={awaiting.has(session.id)} />
                ))}
                {sorted.length > visibleCount && (
                  <button
                    type="button"
                    onClick={() => setShown(String(visibleCount + PAGE_SIZE))}
                    className="mt-2 w-full rounded-lg border py-2 text-center text-xs font-medium transition-colors"
                    style={{ borderColor: 'var(--color-border-2)', backgroundColor: 'var(--color-surface-2)', color: 'var(--color-text-2)' }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--color-brand)'; e.currentTarget.style.color = 'var(--color-text)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--color-border-2)'; e.currentTarget.style.color = 'var(--color-text-2)'; }}
                  >
                    Show more ({sorted.length - visibleCount} remaining)
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      </>)}

      {/* Create session dialog */}
      {showCreateDialog && (
        <div
          className="animate-fade-in fixed inset-0 z-50 flex items-center justify-center"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
          onClick={() => setShowCreateDialog(false)}
          onKeyDown={(e) => e.key === 'Escape' && setShowCreateDialog(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Create session"
        >
          <div
            className="animate-scale-in card w-80 rounded-xl p-6 shadow-xl sm:w-96"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={() => {}}
            role="document"
          >
            <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text)' }}>Create Session</h3>

            <div className="mt-4 space-y-4">
              <div>
                <label htmlFor="session-name" className="block text-sm font-medium" style={{ color: 'var(--color-text-2)' }}>
                  Name (optional)
                </label>
                <input
                  id="session-name"
                  type="text"
                  value={newSessionName}
                  onChange={(e) => setNewSessionName(e.target.value)}
                  placeholder="e.g. Feature implementation"
                  className="mt-1 block w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1"
                  style={{ borderColor: 'var(--color-border-2)', backgroundColor: 'var(--color-surface)', color: 'var(--color-text)' }}
                />
              </div>

              <div>
                <label htmlFor="session-model" className="block text-sm font-medium" style={{ color: 'var(--color-text-2)' }}>
                  Model
                </label>
                <Select
                  id="session-model"
                  value={newSessionModel}
                  onChange={(e) => setNewSessionModel(e.target.value as SessionModel)}
                  className="mt-1 block w-full"
                  style={{ color: 'var(--color-text)' }}
                >
                  {MODEL_OPTIONS.map(({ value, label }) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </Select>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <Button variant="ghost" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreate} disabled={createSession.isPending}>
                {createSession.isPending ? 'Creating...' : 'Create'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Settings of a server: one column of sections at a reading measure, each a heading, its state and its action. Two
 * columns cannot stay balanced: several sections appear only when they have something (calendar history, proposals)
 * and the notifications grow with each type sent, so one side always ran long. `#notify-title` and `#cost-title`
 * links land on their section.
 */
function ServerSettings({ serverId, isAdmin }: { serverId: string; isAdmin: boolean }) {
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    // The sections show at once but grow when their data arrives: wait until none is loading, for a few seconds
    let tries = 0;
    const id = window.setInterval(() => {
      const el = document.getElementById(hash.slice(1));
      const settled = !document.querySelector('.settings-section[aria-busy="true"]');
      if ((el && settled) || ++tries > 20) {
        el?.scrollIntoView({ block: 'start', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
        window.clearInterval(id);
      }
    }, 150);
    return () => window.clearInterval(id);
  }, [hash]);
  return (
    <div className="max-w-3xl">
      {/* How this server notifies: today against the cap, the digest schedule, the calendar it follows (#760 step 3) */}
      <NotificationDelivery serverId={serverId} isAdmin={isAdmin} />
      {/* The operator's Slack in this client's workspace (ADR-039 section 8) */}
      <SlackConnection serverId={serverId} isAdmin={isAdmin} />
      {/* This month's costs and where they are heading, against the server's threshold */}
      <MonthlyCost serverId={serverId} isAdmin={isAdmin} />
      {/* What the server's index holds, its facets, the sessions' searches, and its filling (ADR-042) */}
      <IndexStatus serverId={serverId} isAdmin={isAdmin} />
      {/* The secrets the sessions printed before the mask, checked then cleaned (ADR-043 section 3) */}
      <SecretsScrub serverId={serverId} isAdmin={isAdmin} />
      {/* The servers this one may exchange messages with, and the messages decided (ADR-025 step 2) */}
      <ServerMessaging serverId={serverId} isAdmin={isAdmin} />
      {/* Calendar changes decided in the last 7 days (ADR-024) */}
      <CalendarChanges serverId={serverId} isAdmin={isAdmin} part="history" />
      {/* Proposals for aidev decided in the last 30 days (ADR-025) */}
      <ImprovementProposals serverId={serverId} isAdmin={isAdmin} part="history" />
    </div>
  );
}
